import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Play, Pause, Trash2, User, Upload, Copy, ExternalLink, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [campaignContacts, setCampaignContacts] = useState([]);
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState(null); // null, 'pendente', 'enviado'
  const [loading, setLoading] = useState(true);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editCampaignDialogOpen, setEditCampaignDialogOpen] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [editCampaignForm, setEditCampaignForm] = useState({
    name: '',
    description: ''
  });
  
  // Filtros de categoria
  const [categoryFilters, setCategoryFilters] = useState({
    aluno: true,
    não_aluno: true,
    colaborador: true,
    profissional: true,
  });
  const fileInputRef = useRef(null);
  const [filePreview, setFilePreview] = useState(null);
  const [messageForm, setMessageForm] = useState({
    type: 'text',
    content: '',
    order: 1,
  });

  useEffect(() => {
    fetchCampaign();
    fetchContacts();
  }, [id]);

  useEffect(() => {
    if (campaign) {
      fetchCampaignContacts();
    }
  }, [deliveryStatusFilter, campaign]);

  // Auto-refresh a cada 1 minuto
  useEffect(() => {
    const intervalId = setInterval(() => {
      console.log('Auto-refresh: Recarregando dados da campanha...');
      fetchCampaign();
      if (campaign) {
        fetchCampaignContacts();
      }
    }, 60000); // 60000ms = 1 minuto

    // Cleanup: limpar o intervalo quando o componente desmontar
    return () => clearInterval(intervalId);
  }, [campaign]); // Dependência: campaign (para ter acesso ao ID)

  // Helper function to get public media URL
  const getPublicMediaUrl = (message) => {
    if (!message || !message.content) return '';
    
    // If it's already a full URL, return as is
    if (message.content.startsWith('http')) {
      return message.content;
    }
    
    // Build public URL for local uploads
    const baseUrl = window.location.origin;
    return `${baseUrl}/campaign/${campaign.id}/midia/${message.id}`;
  };

  // Helper function to get media URL for preview (uses backend URL)
  const getMediaPreviewUrl = (message) => {
    if (!message || !message.content) return '';
    
    // If it's already a full URL, return as is
    if (message.content.startsWith('http')) {
      return message.content;
    }
    
    // For local files, use backend URL
    return `${BACKEND_URL}${message.content}`;
  };

  const copyPublicUrl = (message) => {
    const url = getPublicMediaUrl(message);
    navigator.clipboard.writeText(url);
    toast.success('URL copiada para área de transferência!');
  };

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${id}`);
      setCampaign(response.data);
      setSelectedContacts(response.data.contact_ids || []);
      setLastUpdate(new Date()); // Atualizar timestamp
      fetchCampaignContacts(); // Fetch contacts with delivery status
    } catch (error) {
      toast.error('Erro ao carregar campanha');
      navigate('/campaigns');
    }
    setLoading(false);
  };

  const fetchCampaignContacts = async () => {
    try {
      const params = deliveryStatusFilter ? { status_filter: deliveryStatusFilter } : {};
      const response = await axios.get(`${API}/campaigns/${id}/contacts`, { params });
      setCampaignContacts(response.data);
      setLastUpdate(new Date()); // Atualizar timestamp
    } catch (error) {
      console.error('Erro ao carregar contatos da campanha:', error);
    }
  };

  const toggleContactDeliveryStatus = async (contactId, currentStatus) => {
    const newStatus = currentStatus === 'enviado' ? 'pendente' : 'enviado';
    try {
      await axios.patch(`${API}/campaigns/${id}/contacts/${contactId}/delivery-status?new_status=${newStatus}`);
      toast.success(`Status alterado para: ${newStatus === 'enviado' ? 'Enviado' : 'Pendente'}`);
      fetchCampaignContacts();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, {
        params: { per_page: 5000 } // Get all contacts for campaign selection
      });
      // The API now returns paginated data, so we need to extract the contacts array
      const contactsData = response.data.contacts || response.data;
      setContacts(Array.isArray(contactsData) ? contactsData : []);
    } catch (error) {
      toast.error('Erro ao carregar contatos');
      setContacts([]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return null;

    const maxUploadBytes = 200 * 1024 * 1024;
    if (file.size > maxUploadBytes) {
      toast.error('Arquivo muito grande. O tamanho máximo permitido é 200MB.');
      return null;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setUploading(false);
      return response.data.url;
    } catch (error) {
      setUploading(false);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || 'Erro ao fazer upload do arquivo');
      return null;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFilePreview(null);
      return;
    }
    
    // Create preview for the file
    const fileUrl = URL.createObjectURL(file);
    setFilePreview(fileUrl);
  };

  const handleAddMessage = async (e) => {
    e.preventDefault();
    
    let contentToSave = messageForm.content;
    
    // If it's not text, upload the file first
    if (messageForm.type !== 'text' && fileInputRef.current?.files[0]) {
      const uploadedUrl = await handleFileUpload(fileInputRef.current.files[0]);
      if (!uploadedUrl) {
        return; // Upload failed
      }
      contentToSave = uploadedUrl;
    }
    
    try {
      await axios.post(`${API}/campaigns/${id}/messages`, {
        ...messageForm,
        content: contentToSave
      });
      toast.success('Mensagem adicionada!');
      setMessageDialogOpen(false);
      setMessageForm({ type: 'text', content: '', order: (campaign?.messages?.length || 0) + 1 });
      setFilePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchCampaign();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || 'Erro ao adicionar mensagem');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Deseja excluir esta mensagem?')) return;
    try {
      await axios.delete(`${API}/messages/${messageId}`);
      toast.success('Mensagem excluída!');
      fetchCampaign();
    } catch (error) {
      toast.error('Erro ao excluir mensagem');
    }
  };

  // Função para obter contatos filtrados
  const getFilteredContacts = () => {
    if (!Array.isArray(contacts)) return [];
    
    return contacts.filter(contact => {
      const categoria = contact.categoria || 'não_aluno';
      return categoryFilters[categoria];
    });
  };

  // Função para marcar todos os contatos filtrados
  const handleSelectAllFiltered = () => {
    const filteredContacts = getFilteredContacts();
    const filteredIds = filteredContacts.map(c => c.id);
    setSelectedContacts(filteredIds);
  };

  // Função para desmarcar todos
  const handleDeselectAll = () => {
    setSelectedContacts([]);
  };

  // Toggle individual contact
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleOpenEditCampaign = () => {
    setEditCampaignForm({
      name: campaign.name,
      description: campaign.description || ''
    });
    setEditCampaignDialogOpen(true);
  };

  const handleSaveCampaignInfo = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/campaigns/${id}`, {
        name: editCampaignForm.name,
        description: editCampaignForm.description
      });
      toast.success('Campanha atualizada com sucesso!');
      setEditCampaignDialogOpen(false);
      fetchCampaign();
    } catch (error) {
      toast.error('Erro ao atualizar campanha');
    }
  };

  const handleUpdateContacts = async () => {
    try {
      await axios.put(`${API}/campaigns/${id}`, { contact_ids: selectedContacts });
      toast.success('Contatos atualizados!');
      setContactDialogOpen(false);
      fetchCampaign();
    } catch (error) {
      toast.error('Erro ao atualizar contatos');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (newStatus === 'running') {
      if (!campaign.messages || campaign.messages.length === 0) {
        toast.error('Adicione pelo menos uma mensagem antes de executar');
        return;
      }
      if (!campaign.contact_ids || campaign.contact_ids.length === 0) {
        toast.error('Selecione pelo menos um contato antes de executar');
        return;
      }
    }
    
    try {
      await axios.patch(`${API}/campaigns/${id}/status?new_status=${newStatus}`);
      const statusMessages = {
        running: 'Campanha em execução!',
        paused: 'Campanha pausada',
        completed: 'Campanha concluída'
      };
      toast.success(statusMessages[newStatus] || 'Status atualizado');
      fetchCampaign();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || 'Erro ao atualizar status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Deseja realmente excluir esta campanha?')) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      toast.success('Campanha excluída!');
      navigate('/campaigns');
    } catch (error) {
      toast.error('Erro ao excluir campanha');
    }
  };

  const getMessageTypeLabel = (type) => {
    const labels = {
      text: 'Texto',
      image: 'Imagem',
      audio: 'Áudio',
      video: 'Vídeo',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="campaign-detail-page">
      <div className="mb-8">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/campaigns')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>

        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div className="flex-1">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">{campaign.name}</h1>
            <p className="text-gray-600">{campaign.description || 'Sem descrição'}</p>
            <p className="text-xs text-gray-500 mt-1">
              🔄 Última atualização: {lastUpdate.toLocaleTimeString('pt-BR')} • Atualização automática a cada 1 minuto
            </p>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {/* Status Badge - SEMPRE VISÍVEL */}
            <div className="flex items-center gap-2">
              {campaign.status === 'running' && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium whitespace-nowrap">
                  ▶️ Em Execução
                </span>
              )}
              {campaign.status === 'paused' && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium whitespace-nowrap">
                  ⏸️ Pausada
                </span>
              )}
              {campaign.status === 'completed' && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium whitespace-nowrap">
                  ✅ Concluída
                </span>
              )}
              {campaign.status === 'draft' && (
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium whitespace-nowrap">
                  📝 Rascunho
                </span>
              )}
            </div>

            {/* Botão Executar - aparece quando pausada, concluída ou draft */}
            {(campaign.status === 'paused' || campaign.status === 'completed' || campaign.status === 'draft') && (
              <Button
                data-testid="execute-campaign-button"
                onClick={() => handleUpdateStatus('running')}
                className="bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white whitespace-nowrap"
              >
                <Play className="w-4 h-4 mr-2" />
                Executar
              </Button>
            )}

            {/* Botão Pausar - aparece quando em execução */}
            {campaign.status === 'running' && (
              <Button
                data-testid="pause-campaign-button"
                onClick={() => handleUpdateStatus('paused')}
                variant="outline"
                className="border-yellow-600 text-yellow-700 hover:bg-yellow-50 whitespace-nowrap"
              >
                <Pause className="w-4 h-4 mr-2" />
                Pausar
              </Button>
            )}

            {/* Botão Editar */}
            <Button
              onClick={handleOpenEditCampaign}
              variant="outline"
              className="border-gray-400 text-gray-700 hover:bg-gray-50 whitespace-nowrap"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </Button>

            <Button
              data-testid="delete-campaign-button"
              onClick={handleDelete}
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages Section */}
        <div className="lg:col-span-2">
          <Card className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Mensagens</CardTitle>
              <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-message-button"
                    size="sm"
                    className="bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Adicionar Mensagem</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddMessage} className="space-y-4">
                    <div>
                      <Label htmlFor="type">Tipo</Label>
                      <Select
                        value={messageForm.type}
                        onValueChange={(value) => {
                          setMessageForm({ ...messageForm, type: value });
                          setFilePreview(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                      >
                        <SelectTrigger data-testid="message-type-select" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent position="popper" className="z-[100]" side="bottom" align="start">
                          <SelectItem value="text">Texto</SelectItem>
                          <SelectItem value="image">Imagem</SelectItem>
                          <SelectItem value="audio">Áudio</SelectItem>
                          <SelectItem value="video">Vídeo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      {messageForm.type === 'text' ? (
                        <>
                          <Label htmlFor="content">Mensagem</Label>
                          <Textarea
                            id="content"
                            data-testid="message-content-input"
                            value={messageForm.content}
                            onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                            required
                            rows={4}
                          />
                        </>
                      ) : (
                        <>
                          <Label htmlFor="file">Fazer Upload do Arquivo</Label>
                          <Input
                            ref={fileInputRef}
                            id="file"
                            type="file"
                            data-testid="message-file-input"
                            onChange={handleFileChange}
                            accept={
                              messageForm.type === 'image' ? 'image/*' :
                              messageForm.type === 'video' ? 'video/*' :
                              messageForm.type === 'audio' ? 'audio/*' : '*'
                            }
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {messageForm.type === 'image' && 'Formatos: JPG, PNG, GIF, WebP'}
                            {messageForm.type === 'video' && 'Formatos: MP4, MPEG, MOV, AVI'}
                            {messageForm.type === 'audio' && 'Formatos: MP3, WAV, OGG, AAC'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Tamanho máximo: 200MB</p>
                          
                          {/* Preview do arquivo */}
                          {filePreview && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <Label className="text-sm font-semibold mb-2 block">Preview:</Label>
                              {messageForm.type === 'image' && (
                                <img 
                                  src={filePreview} 
                                  alt="Preview" 
                                  className="max-w-full h-auto max-h-64 rounded-lg border border-gray-300"
                                />
                              )}
                              {messageForm.type === 'video' && (
                                <video 
                                  src={filePreview} 
                                  controls 
                                  className="max-w-full h-auto max-h-64 rounded-lg border border-gray-300"
                                />
                              )}
                              {messageForm.type === 'audio' && (
                                <audio 
                                  src={filePreview} 
                                  controls 
                                  className="w-full"
                                />
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <Button
                      data-testid="message-submit-button"
                      type="submit"
                      disabled={uploading}
                      className="w-full bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
                    >
                      {uploading ? 'Fazendo upload...' : 'Adicionar Mensagem'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {!campaign.messages || campaign.messages.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Nenhuma mensagem adicionada</p>
              ) : (
                <div className="space-y-3">
                  {campaign.messages
                    .sort((a, b) => a.order - b.order)
                    .map((message, index) => (
                      <div
                        key={message.id}
                        data-testid="message-item"
                        className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-700 text-white flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">{getMessageTypeLabel(message.type)}</Badge>
                          </div>
                          
                          {/* Preview da mensagem */}
                          {message.type === 'text' ? (
                            <p className="text-sm text-gray-700">{message.content}</p>
                          ) : message.type === 'image' ? (
                            <div className="space-y-2">
                              <img 
                                src={getMediaPreviewUrl(message)} 
                                alt="Mensagem de imagem" 
                                className="max-w-xs h-auto max-h-48 rounded-lg border border-gray-300"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImagem%3C/text%3E%3C/svg%3E';
                                }}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyPublicUrl(message)}
                                className="text-xs"
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copiar URL Pública
                              </Button>
                            </div>
                          ) : message.type === 'video' ? (
                            <div className="space-y-2">
                              <video 
                                src={getMediaPreviewUrl(message)} 
                                controls 
                                className="max-w-xs h-auto max-h-48 rounded-lg border border-gray-300"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyPublicUrl(message)}
                                className="text-xs"
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copiar URL Pública
                              </Button>
                            </div>
                          ) : message.type === 'audio' ? (
                            <div className="space-y-2">
                              <audio 
                                src={getMediaPreviewUrl(message)} 
                                controls 
                                className="w-full max-w-xs"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyPublicUrl(message)}
                                className="text-xs"
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copiar URL Pública
                              </Button>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-700">{message.content}</p>
                          )}
                        </div>
                        <Button
                          data-testid="delete-message-button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteMessage(message.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Contacts Section */}
        <div>
          <Card className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Contatos ({campaign.contact_ids?.length || 0})</CardTitle>
              <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="select-contacts-button"
                    size="sm"
                    variant="outline"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Selecionar
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0">
                  {/* Header Fixo */}
                  <div className="px-6 py-4 border-b bg-white">
                    <DialogHeader>
                      <DialogTitle>Selecionar Contatos</DialogTitle>
                    </DialogHeader>
                  </div>
                  
                  {/* Filtros e Estatísticas - Fixo */}
                  <div className="px-6 py-4 border-b bg-gray-50 space-y-3">
                    {/* Filtros por Categoria */}
                    <div>
                      <label className="text-sm font-semibold text-gray-900 mb-2 block">
                        Filtrar por Categoria:
                      </label>
                      <div className="grid grid-cols-4 gap-3">
                        <label className="flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-colors"
                          style={{ 
                            borderColor: categoryFilters.aluno ? '#3b82f6' : '#e5e7eb',
                            backgroundColor: categoryFilters.aluno ? '#eff6ff' : 'white'
                          }}
                        >
                          <Checkbox
                            checked={categoryFilters.aluno}
                            onCheckedChange={(checked) => 
                              setCategoryFilters({...categoryFilters, aluno: checked})
                            }
                          />
                          <span className="text-sm font-medium">🎓 Alunos</span>
                          <span className="ml-auto text-xs font-semibold text-blue-600">
                            {Array.isArray(contacts) ? contacts.filter(c => c.categoria === 'aluno').length : 0}
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-colors"
                          style={{ 
                            borderColor: categoryFilters.não_aluno ? '#6b7280' : '#e5e7eb',
                            backgroundColor: categoryFilters.não_aluno ? '#f9fafb' : 'white'
                          }}
                        >
                          <Checkbox
                            checked={categoryFilters.não_aluno}
                            onCheckedChange={(checked) => 
                              setCategoryFilters({...categoryFilters, não_aluno: checked})
                            }
                          />
                          <span className="text-sm font-medium">👤 Não Alunos</span>
                          <span className="ml-auto text-xs font-semibold text-gray-600">
                            {Array.isArray(contacts) ? contacts.filter(c => c.categoria === 'não_aluno').length : 0}
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-colors"
                          style={{ 
                            borderColor: categoryFilters.colaborador ? '#9333ea' : '#e5e7eb',
                            backgroundColor: categoryFilters.colaborador ? '#faf5ff' : 'white'
                          }}
                        >
                          <Checkbox
                            checked={categoryFilters.colaborador}
                            onCheckedChange={(checked) => 
                              setCategoryFilters({...categoryFilters, colaborador: checked})
                            }
                          />
                          <span className="text-sm font-medium">💼 Colaboradores</span>
                          <span className="ml-auto text-xs font-semibold text-purple-600">
                            {Array.isArray(contacts) ? contacts.filter(c => c.categoria === 'colaborador').length : 0}
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-colors"
                          style={{ 
                            borderColor: categoryFilters.profissional ? '#f97316' : '#e5e7eb',
                            backgroundColor: categoryFilters.profissional ? '#fff7ed' : 'white'
                          }}
                        >
                          <Checkbox
                            checked={categoryFilters.profissional}
                            onCheckedChange={(checked) => 
                              setCategoryFilters({...categoryFilters, profissional: checked})
                            }
                          />
                          <span className="text-sm font-medium">👔 Profissionais</span>
                          <span className="ml-auto text-xs font-semibold text-orange-600">
                            {Array.isArray(contacts) ? contacts.filter(c => c.categoria === 'profissional').length : 0}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Estatísticas e Ações */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Exibindo:</span>
                          <span className="font-semibold text-gray-900 ml-1">
                            {getFilteredContacts().length}
                          </span>
                          <span className="text-gray-600 ml-1">contatos</span>
                        </div>
                        <div className="h-4 w-px bg-gray-300"></div>
                        <div>
                          <span className="text-gray-600">Selecionados:</span>
                          <span className="font-semibold text-emerald-700 ml-1">
                            {selectedContacts.length}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleSelectAllFiltered}
                          className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                        >
                          ✓ Marcar Todos Filtrados
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleDeselectAll}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          ✗ Desmarcar Todos
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Lista de Contatos - Scrollable */}
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    <div className="space-y-2">
                      {getFilteredContacts().length === 0 ? (
                        <div className="text-center py-12">
                          <p className="text-gray-500">Nenhum contato corresponde aos filtros selecionados</p>
                          <p className="text-sm text-gray-400 mt-2">Selecione pelo menos uma categoria acima</p>
                        </div>
                      ) : (
                        getFilteredContacts().map((contact) => {
                          const getCategoriaStyle = (categoria) => {
                            switch(categoria) {
                              case 'aluno':
                                return 'bg-blue-100 text-blue-700 border-blue-200';
                              case 'colaborador':
                                return 'bg-purple-100 text-purple-700 border-purple-200';
                              case 'profissional':
                                return 'bg-orange-100 text-orange-700 border-orange-200';
                              default:
                                return 'bg-gray-100 text-gray-700 border-gray-200';
                            }
                          };
                          
                          const getCategoriaIcon = (categoria) => {
                            switch(categoria) {
                              case 'aluno': return '🎓';
                              case 'colaborador': return '💼';
                              case 'profissional': return '👔';
                              default: return '👤';
                            }
                          };
                          
                          const getCategoriaLabel = (categoria) => {
                            switch(categoria) {
                              case 'aluno': return 'Aluno';
                              case 'colaborador': return 'Colaborador';
                              case 'profissional': return 'Profissional';
                              default: return 'Não Aluno';
                            }
                          };
                          
                          return (
                            <div
                              key={contact.id}
                              className={`flex items-center gap-3 p-3 border-2 rounded-lg transition-all cursor-pointer ${
                                selectedContacts.includes(contact.id) 
                                  ? 'bg-emerald-50 border-emerald-500' 
                                  : 'bg-white border-gray-200 hover:border-gray-300'
                              }`}
                              onClick={() => toggleContactSelection(contact.id)}
                            >
                              <Checkbox
                                data-testid="contact-checkbox"
                                checked={selectedContacts.includes(contact.id)}
                                onCheckedChange={() => toggleContactSelection(contact.id)}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium text-sm truncate">{contact.name}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${getCategoriaStyle(contact.categoria)}`}>
                                    {getCategoriaIcon(contact.categoria)} {getCategoriaLabel(contact.categoria)}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600">{contact.phone}</p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  
                  {/* Footer Fixo com Botão de Ação */}
                  <div className="px-6 py-4 border-t bg-white">
                    <Button
                      data-testid="update-contacts-button"
                      onClick={handleUpdateContacts}
                      disabled={selectedContacts.length === 0}
                      className="w-full bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Salvar Seleção ({selectedContacts.length} {selectedContacts.length === 1 ? 'contato' : 'contatos'})
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {/* Filtros de Status de Entrega */}
              {campaign.contact_ids && campaign.contact_ids.length > 0 && (
                <div className="mb-4 flex gap-2">
                  <Button
                    size="sm"
                    variant={deliveryStatusFilter === null ? "default" : "outline"}
                    onClick={() => setDeliveryStatusFilter(null)}
                    className={deliveryStatusFilter === null ? "bg-emerald-700" : ""}
                  >
                    Todos ({campaign.contact_ids.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={deliveryStatusFilter === 'pendente' ? "default" : "outline"}
                    onClick={() => setDeliveryStatusFilter('pendente')}
                    className={deliveryStatusFilter === 'pendente' ? "bg-yellow-600" : ""}
                  >
                    ⏳ Pendentes
                  </Button>
                  <Button
                    size="sm"
                    variant={deliveryStatusFilter === 'enviado' ? "default" : "outline"}
                    onClick={() => setDeliveryStatusFilter('enviado')}
                    className={deliveryStatusFilter === 'enviado' ? "bg-green-600" : ""}
                  >
                    ✅ Enviados
                  </Button>
                </div>
              )}
              
              {!campaign.contact_ids || campaign.contact_ids.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">Nenhum contato selecionado</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {campaignContacts.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">
                      Nenhum contato com status "{deliveryStatusFilter}"
                    </p>
                  ) : (
                    campaignContacts.map((contact) => (
                      <div
                        key={contact.id}
                        data-testid="selected-contact-item"
                        className="flex items-center justify-between p-3 bg-gray-50 rounded text-sm hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <User className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium">{contact.name}</p>
                            <p className="text-xs text-gray-600">{contact.phone}</p>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => toggleContactDeliveryStatus(contact.id, contact.delivery_status)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                            contact.delivery_status === 'enviado'
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          }`}
                        >
                          {contact.delivery_status === 'enviado' ? '✅ Enviado' : '⏳ Pendente'}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog para Editar Nome e Descrição da Campanha */}
      <Dialog open={editCampaignDialogOpen} onOpenChange={setEditCampaignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Campanha</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCampaignInfo} className="space-y-4">
            <div>
              <Label htmlFor="campaign-name">Nome da Campanha</Label>
              <Input
                id="campaign-name"
                value={editCampaignForm.name}
                onChange={(e) => setEditCampaignForm({ ...editCampaignForm, name: e.target.value })}
                placeholder="Digite o nome da campanha"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="campaign-description">Descrição</Label>
              <Textarea
                id="campaign-description"
                value={editCampaignForm.description}
                onChange={(e) => setEditCampaignForm({ ...editCampaignForm, description: e.target.value })}
                placeholder="Digite a descrição da campanha (opcional)"
                rows={4}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditCampaignDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
              >
                Salvar Alterações
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignDetail;
